import React, { useState } from 'react';
import { BiPencil, BiPlus } from 'react-icons/bi';
import { Avatar } from 'flowbite-react';
import ReadMoreText from '../shared/ReadMoreText';
import DeleteProfileDetails from './DeleteProfileDetails';
import { timeGapConvertor } from 'shared-utils';
import { UIUserWorkExperienceDetail, WorkExperienceDetail } from 'types';
import EditProfileWorkExperience from './EditProfileWorkExperience';
import useDisclosure from 'src/hooks/utils/useDisclosure';
import { useMutation, useQueryClient } from 'react-query';
import { deleteUserWorkExperienceFn } from 'src/apis/profile';
import { useToast } from 'src/context/ToastProvider';
import { get } from 'lodash';
import ProfileContentHeader from './ProfileContentHeader';

interface Props {
	title: string;
	editing?: boolean;
	workExperience: UIUserWorkExperienceDetail[];
	profileId?: String;
	id?: string;
	isSelf?: boolean | null;
	isPublicProfile: boolean;
}

const ProfileWorkExperience = ({
	title,
	isSelf,
	isPublicProfile,
	editing = false,
	workExperience,
	profileId,
	id,
}: Props) => {
	const toast = useToast();
	const queryClient = useQueryClient();
	const [deletingWorkExperience, setDeletingWorkExperienceIndex] = useState(-1);
	const [isEditing, setIsEditing] = useState(false);
	const { isOpen, onOpen, onClose } = useDisclosure();
	const [deleteModalStates, setDeleteModalStates] = useState<Array<boolean>>(workExperience.map(() => false));
	const [selectedWorkExpId, setSelectedWorkExpId] = useState<string>('');
	const [userWorkExperience, setUserWorkExperience] = useState<WorkExperienceDetail>({
		company: '',
		description: '',
		endedAt: new Date(),
		jobTitle: '',
		startedFrom: new Date(),
		workLocation: '',
	});

	const deleteWorkExperienceFn = useMutation({
		mutationKey: 'deleteWorkExperience',
		mutationFn: deleteUserWorkExperienceFn,
		onSuccess() {
			queryClient.refetchQueries(['getUserProfile']);
			toast({
				title: 'Work experience deleted successfully',
				type: 'success',
			});
			const newStates = [...deleteModalStates];
			newStates[deletingWorkExperience] = false;
			setDeleteModalStates(workExperience.map(() => false));
			setDeletingWorkExperienceIndex(-1);
		},
		onError(error: Error) {
			const errorMessage = get(error, ['response', 'data', 'message'], error.message);
			toast({
				type: 'error',
				title: errorMessage,
			});
		},
	});

	const handleWorkExperienceDelete = (id: string, index: number) => {
		setDeletingWorkExperienceIndex(index);
		deleteWorkExperienceFn.mutate(id);
	};

	const handleWorkExperience = (event: React.MouseEvent<HTMLDivElement, MouseEvent>) => {
		event.stopPropagation();
		setIsEditing(false);
		setUserWorkExperience({
			...userWorkExperience,
			company: '',
			description: '',
			endedAt: new Date(),
			jobTitle: '',
			workLocation: '',
			startedFrom: new Date(),
		});
		onOpen();
	};

	return (
		<>
			<ProfileContentHeader
				data={{ name: title, isImp: false }}
				extraContent={
					isSelf ? (
						<div className="ml-auto flex grow-0 items-center justify-end bg-transparent pb-1 mr-1 mt-1">
							{/* Add new work experience button  */}
							<div
								onClick={event => handleWorkExperience(event)}
								className="flex items-center justify-center gap-[4px] rounded-[4px] border border-[#C7C9D9] px-[4px] py-[2px] text-[11px] text-[#111928] dark:text-[white] hover:cursor-pointer hover:border-[black] bg-white dark:bg-inherit dark:hover:bg-[white] hover:!text-[black]"
							>
								<BiPlus size={15} />
							</div>
						</div>
					) : null
				}
			/>
			<div
				id={id}
				className="flex w-full max-w-full flex-col gap-2 overflow-hidden bg-transparent bg-white dark:bg-[#1F2A37] rounded-[8px] relative"
			>
				{workExperience && workExperience.length !== 0 ? (
					<div className="flex grow flex-col gap-[14px] p-1">
						{workExperience
							?.filter(wE => wE.jobTitle)
							.map((userwork: UIUserWorkExperienceDetail, index: number) => {
								const workDuration = timeGapConvertor(userwork.startedFrom, userwork.endedAt);
								return (
									<div
										key={userwork._id}
										className="p-3 bg-white dark:bg-gray-800 rounded-[8px] md:group-hover:show-icons-on-hover group flex gap-[14px] font-inter text-[16px] font-[500] sm:flex"
									>
										<div className="min-w-[45px] pt-1">
											<Avatar img="/assets/company_logo.svg" rounded={true} bordered={true} />
										</div>
										<div className="w-[calc(100%-64px)] whitespace-normal break-words">
											<div className="flex w-full justify-between">
												<p className="w-[85%] whitespace-normal break-words text-[#7777E7] dark:text-[#7777E7] sm:w-[80%]">
													{userwork.jobTitle}
												</p>

												{isSelf ? (
													<div className="edit-delete-icon gap text-[#28293D] dark:text-[#c2c3db] sm:flex sm:gap-2 md:hidden md:group-hover:flex md:group-hover:gap-3">
														<div>
															<BiPencil
																onClick={e => {
																	e.stopPropagation();
																	setSelectedWorkExpId(userwork._id);
																	setUserWorkExperience({
																		...userWorkExperience,
																		company: userwork.company || '',
																		description: userwork.description || '',
																		endedAt: userwork.endedAt || new Date(),
																		jobTitle: userwork.jobTitle || '',
																		workLocation: userwork.workLocation || '',
																		startedFrom: userwork.startedFrom || new Date(),
																	});
																	setIsEditing(true);
																	onOpen();
																}}
																className="cursor-pointer"
															/>
														</div>
														<div>
															<DeleteProfileDetails
																onDelete={handleWorkExperienceDelete}
																id={userwork._id}
																index={index}
																isDeleteModalOpen={deleteModalStates[index]}
																setIsDeleteModalOpen={isOpen => {
																	const newStates = [...deleteModalStates];
																	newStates[index] = isOpen;
																	setDeleteModalStates(newStates);
																}}
																isLoading={false}
															/>
														</div>
													</div>
												) : null}
											</div>
											{userwork.company ? (
												<p className={`${editing ? 'max-w-[90%]' : 'max-w-[100%]'} text-[#28293D] dark:text-[#E4E4EB]`}>
													{userwork.company}
												</p>
											) : null}
											<div className="flex w-full gap-[4px]  text-[12px] font-[400] text-[#555770] dark:text-[#d5d5da]">
												{userwork.workLocation ? (
													<p className="truncate md:max-w-[30%]">{userwork.workLocation}</p>
												) : null}
												<ul className="list-disc truncate pl-[20px]">
													{workDuration && !workDuration.includes('NaN') ? <li>{workDuration} exp.</li> : <div />}
												</ul>
											</div>
											<p
												className={`${
													editing ? 'max-w-[90%]' : 'max-w-[100%]'
												} text-[15px] font-[400] text-[#555770] dark:text-[#C7C9D9]`}
											>
												{userwork.description && userwork.description?.length > 250 ? (
													<ReadMoreText maxChars={220} work_description={userwork.description || ''} />
												) : (
													<>{userwork.description}</>
												)}
											</p>
										</div>
									</div>
								);
							})}
					</div>
				) : (
					<div className="px-4 py-5 text-center font-inter text-[14px] font-[500] text-[#555770] dark:text-[#C7C9D9]">
						{isSelf && !isPublicProfile
							? 'Add your work experience and let the world marvel at your tech skills.'
							: "There's nothing to show."}
					</div>
				)}
				<EditProfileWorkExperience
					isOpen={isOpen}
					onClose={onClose}
					workExperience={userWorkExperience}
					isEditing={isEditing}
					workExpId={selectedWorkExpId}
				/>
			</div>
		</>
	);
};

export default ProfileWorkExperience;
